import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { EnsApi, EnsName, EnsWallet } from '../../../application/ens/ports';
import { changeAddress, changeAvatar, Failure, mintName, MintStep } from '../../../application/ens/ensUseCases';
import { fullName, priceOf, sanitizeLabel, shortAddress } from '../../../domain/ens/ens';
import { Tone } from '../shared';

/** Everything /ens shows and does, for the desktop and the phone layouts alike. */

export type Message = { tone: Tone; text: string } | null;
type Load<T> = { status: 'loading' | 'ready' | 'error'; data: T };

const STEP_TEXT: Record<MintStep, string> = {
  checking: 'Checking the name is free...',
  signing: 'Sign the message in your wallet to prove it is yours. It costs nothing.',
  paying: 'Confirm the payment in your wallet, then wait for the block...',
  minting: 'Minting your name, no gas needed...',
};

export function failureText(f: Failure, label = ''): string {
  switch (f.reason) {
    case 'no-wallet':
      return 'Connect your wallet first.';
    case 'no-name':
      return 'Type a name first.';
    case 'taken':
      return `${fullName(label)} is already taken. Try another one.`;
    case 'rejected':
      return 'You cancelled it in your wallet. Nothing changed.';
    case 'already-free':
      return 'This wallet already has its free name. Names under 10 characters can still be bought.';
    case 'bad-address':
      return 'That is not a valid Ethereum address.';
    case 'not-image':
      return 'Pick an image file.';
    case 'too-big':
      return 'The image must be under 5 MB.';
    default:
      return f.message;
  }
}

export function useEnsPage({ api, wallet }: { api: EnsApi; wallet: EnsWallet }) {
  const address = wallet.address;
  const [ethUsd, setEthUsd] = useState(0);
  const [recent, setRecent] = useState<Load<{ names: EnsName[]; total: number }>>({ status: 'loading', data: { names: [], total: 0 } });
  const [mine, setMine] = useState<Load<EnsName[]>>({ status: 'ready', data: [] });
  const [label, setLabelRaw] = useState('');
  const [step, setStep] = useState<MintStep | null>(null);
  const [mintMsg, setMintMsg] = useState<Message>(null);
  const [minted, setMinted] = useState<string | null>(null);
  const alive = useRef(true);
  useEffect(() => () => {
    alive.current = false;
  }, []);

  useEffect(() => {
    let stop = false;
    const read = () =>
      api
        .ethUsd()
        .then((v) => !stop && setEthUsd(v))
        .catch(() => {});
    read();
    const t = setInterval(read, 60000);
    return () => {
      stop = true;
      clearInterval(t);
    };
  }, [api]);

  const loadRecent = useCallback(() => {
    setRecent((r) => ({ ...r, status: 'loading' }));
    api
      .recent()
      .then((data) => alive.current && setRecent({ status: 'ready', data }))
      .catch(() => alive.current && setRecent((r) => ({ ...r, status: 'error' })));
  }, [api]);

  const loadMine = useCallback(() => {
    if (!address) {
      setMine({ status: 'ready', data: [] });
      return;
    }
    setMine((m) => ({ ...m, status: 'loading' }));
    api
      .mine(address)
      .then((data) => alive.current && setMine({ status: 'ready', data }))
      .catch(() => alive.current && setMine((m) => ({ ...m, status: 'error' })));
  }, [api, address]);

  useEffect(loadRecent, [loadRecent]);
  useEffect(loadMine, [loadMine]);

  const price = useMemo(() => priceOf(label, ethUsd), [label, ethUsd]);

  const setLabel = (value: string) => {
    setLabelRaw(sanitizeLabel(value));
    setMintMsg(null);
    setMinted(null);
  };

  const mint = async () => {
    setMinted(null);
    const r = await mintName({
      label,
      ethUsd,
      api,
      wallet,
      onStep: (s) => {
        setStep(s);
        setMintMsg({ tone: 'info', text: STEP_TEXT[s] });
      },
    });
    if (!alive.current) return;
    setStep(null);
    if (r.ok) {
      setMinted(r.name);
      setMintMsg({ tone: 'good', text: `${r.name} is yours. Point it anywhere or give it an avatar from Your names.` });
      setLabelRaw('');
      loadMine();
      loadRecent();
    } else {
      setMintMsg({ tone: 'bad', text: failureText(r, label) });
    }
  };

  /* -------------------------------------------------- manage one of your names */

  const [managing, setManaging] = useState<EnsName | null>(null);
  const [manageBusy, setManageBusy] = useState(false);
  const [manageMsg, setManageMsg] = useState<Message>(null);

  const openManage = (n: EnsName | null) => {
    setManaging(n);
    setManageMsg(null);
  };

  const moveTo = async (newAddress: string) => {
    if (!managing) return;
    setManageBusy(true);
    setManageMsg({ tone: 'info', text: 'Sign the change in your wallet...' });
    const r = await changeAddress({ label: managing.label, newAddress: newAddress.trim(), api, wallet });
    if (!alive.current) return;
    setManageBusy(false);
    if (r.ok) {
      setManageMsg({ tone: 'good', text: `${managing.name} now points to ${shortAddress(newAddress.trim())}.` });
      loadMine();
    } else setManageMsg({ tone: 'bad', text: failureText(r) });
  };

  const setAvatar = async (file: File) => {
    if (!managing) return;
    setManageBusy(true);
    setManageMsg({ tone: 'info', text: 'Uploading, then sign the change in your wallet...' });
    const r = await changeAvatar({ label: managing.label, file, api, wallet });
    if (!alive.current) return;
    setManageBusy(false);
    if (r.ok) {
      setManageMsg({ tone: 'good', text: `${managing.name} has a new avatar. Wallets may take a few minutes to show it.` });
      loadMine();
      loadRecent();
    } else setManageMsg({ tone: 'bad', text: failureText(r) });
  };

  return {
    address,
    connected: !!address,
    ethUsd,
    recent,
    loadRecent,
    mine,
    loadMine,
    label,
    setLabel,
    price,
    step,
    busy: step !== null,
    mint,
    mintMsg,
    minted,
    managing,
    openManage,
    manageBusy,
    manageMsg,
    moveTo,
    setAvatar,
  };
}

export type EnsPageState = ReturnType<typeof useEnsPage>;
